import { useState } from 'react';
import { toBlob } from 'html-to-image';
import { useNavigate } from 'react-router-dom';
import { useHub, usePrivate } from '../context';
import { loadWeaponKills } from '../activity';
import { StatTile, useLegend } from '../shared';
import { sameOriginImg } from '../../lib/bungie';
import { Asking, Bar, Heading, Pic, SideLabel } from '../ui';

/** Port of the app's Legend tab: the characters by time, scores, titles earned, exotic kills, lifetime PvE and Crucible. */

const PVE: [string, string][] = [
  ['activitiesCleared', 'Activities cleared'], ['kills', 'Combatants defeated'], ['precisionKills', 'Precision kills'], ['deaths', 'Deaths'], ['assists', 'Assists'],
  ['killsDeathsRatio', 'Kills per death'], ['bestSingleGameKills', 'Most kills in one activity'], ['longestKillSpree', 'Longest kill spree'],
  ['publicEventsCompleted', 'Public events'], ['heroicPublicEventsCompleted', 'Heroic public events'], ['resurrectionsPerformed', 'Revives given'], ['orbsDropped', 'Orbs made'],
];
const PVP: [string, string][] = [
  ['activitiesEntered', 'Matches played'], ['activitiesWon', 'Matches won'], ['winLossRatio', 'Win / loss ratio'], ['kills', 'Guardians defeated'], ['deaths', 'Deaths'],
  ['assists', 'Assists'], ['killsDeathsRatio', 'K/D'], ['efficiency', 'Efficiency (K+A)/D'], ['precisionKills', 'Precision kills'], ['bestSingleGameKills', 'Most kills in one match'],
  ['longestKillSpree', 'Longest kill spree'], ['combatRating', 'Combat rating'],
];

export default function LegendTab() {
  const h = useHub();
  const nav = useNavigate();
  const hub = h.hub!;
  const defs = h.defs;
  const { data: legend, error } = useLegend();
  const { data: weapons } = usePrivate(`weapons`, () => loadWeaponKills(h.player!, hub.chars.map((c) => c.id)), { public: true });
  const [note, setNote] = useState<string>();
  const total = Math.max(1, hub.chars.reduce((s, c) => s + c.minutes, 0));
  const live = (r: number) => hub.record(r, h.char?.id);

  // collection score: owned items under the Collections root
  let collected: { have: number; total: number } | undefined;
  if (defs && hub.hasCollections && defs.roots.collections) {
    const all = new Set<number>();
    const walk = (n: number) => { const d = defs.nodes.get(n); if (!d) return; d.collectibles.forEach((c) => all.add(c)); d.nodes.forEach(walk); };
    walk(defs.roots.collections);
    let have = 0, t = 0;
    for (const c of all) { if (!defs.collectibles.has(c)) continue; const o = hub.owned(c, h.char?.id); if (o < 0) continue; t++; have += o; }
    collected = { have, total: t };
  }
  const earned = defs ? [...(defs.nodes.get(defs.roots.seals)?.nodes ?? []), ...(defs.nodes.get(defs.roots.legacySeals)?.nodes ?? [])]
    .filter((s) => defs.nodes.has(s) && live(defs.nodes.get(s)!.seal)?.done) : [];
  const gilds = defs ? earned.reduce((s, x) => s + Math.max(0, (live(defs.nodes.get(x)!.seal)?.times ?? 1) - 1), 0) : 0;

  // as the app: a picture of this page as it is on screen, saved and copied, ready to paste into Discord
  const share = async () => {
    const root = document.querySelector<HTMLElement>('.g-body');
    if (!root) return;
    setNote('Taking the picture...');
    // Bungie's pictures can't be drawn into a picture from their own site, so for the moment they come through ours
    const swapped: [HTMLImageElement, string][] = [];
    for (const im of root.querySelectorAll('img')) {
      if (!im.src.startsWith('https://www.bungie.net/')) continue;
      swapped.push([im, im.src]);
      im.src = sameOriginImg(new URL(im.src).pathname)!;
    }
    try {
      // each relayed picture loaded (or given up on after 5 s, so a slow one never holds the button up)
      await Promise.all(swapped.map(([im]) => (im.complete ? null : new Promise((done) => {
        im.addEventListener('load', done, { once: true }); im.addEventListener('error', done, { once: true }); setTimeout(done, 5000);
      }))));
      // the page fades in: without this the picture's copy would start that fade again, from invisible
      root.classList.add('g-capturing');
      const blob = await toBlob(root, { backgroundColor: '#100f16', skipFonts: true, filter: (n) => !(n instanceof HTMLElement && n.classList.contains('g-brag')) })
        .finally(() => root.classList.remove('g-capturing'));
      if (!blob) throw new Error('no picture');
      const name = `${hub.name.split('#')[0]} Legend.png`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      let copied = false;
      try { await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); copied = true; } catch { /* the browser said no */ }
      setNote(copied ? 'Picture saved and copied, ready to paste' : 'Picture saved (this browser would not copy it)');
    } catch {
      // the picture failed: a link to the page instead
      try { await navigator.clipboard.writeText(`${hub.name.split('#')[0]}'s Legend: ${location.origin}/guardian/legend`); setNote('Link copied, ready to paste'); }
      catch { setNote('Could not take the picture'); }
    } finally {
      for (const [im, src] of swapped) im.src = src;
    }
  };

  return (
    <>
      <div className="g-banners" style={{ gridTemplateColumns: `repeat(${Math.max(3, hub.chars.length)}, minmax(0, 1fr))` }}>
        {[...hub.chars].sort((a, b) => b.minutes - a.minutes).map((c) => {
          const days = Math.floor(c.minutes / 1440);
          const span = days >= 60 ? `${Math.floor(days / 30)} months, ${Math.floor((days % 30) / 7)} weeks` : days >= 1 ? `${days} days, ${Math.floor((c.minutes % 1440) / 60)} hours` : `${Math.floor(c.minutes / 60)} hours, ${c.minutes % 60} minutes`;
          return (
            <div key={c.id} className="g-stack" style={{ gap: 6 }}>
              <div className="g-legendcard">
                <Pic src={c.banner} className="art" />
                <div className="who"><b>{c.cls}</b><span>{c.race}</span></div>
                <span className="light">{c.light}</span>
              </div>
              <Bar fraction={c.minutes / total} color="#ffffff" height={3} />
              <div className="small"><span>{span.toUpperCase()}</span><span className="g-dim">  /  {Math.round((c.minutes * 100) / total)}%</span></div>
            </div>
          );
        })}
      </div>

      <div className="g-wrap" style={{ marginTop: 24 }}>
        {hub.hasRecords && (
          <>
            <StatTile label="Active Triumph Score" value={hub.activeScore} />
            <StatTile label="Legacy Triumph Score" value={hub.legacyScore} />
            <StatTile label="Lifetime Triumph Score" value={hub.lifetimeScore} color="var(--gold)" />
          </>
        )}
        {collected && <StatTile label="Collection Score" value={collected.have} title={`${collected.have.toLocaleString()} of ${collected.total.toLocaleString()} collection items found`} />}
        <button type="button" className="g-small big g-brag" onClick={share} title="Saves a picture of this page as it is on screen and copies it, ready to paste into Discord">{note ?? 'Brag about it: save a picture'}</button>
      </div>

      {hub.hasRecords && defs && (
        <div className="g-legendblock">
          <SideLabel text="Triumph titles" />
          <div className="g-stack" style={{ gap: 8, paddingRight: 30 }}>
            <div style={{ fontSize: 20 }}>Emblems of victory</div>
            <p className="g-text2" style={{ fontSize: 13.5 }}>The ultimate measure of a completionist and collector; wear them well.</p>
            <div style={{ fontSize: 14 }}>{earned.length} titles earned{gilds > 0 ? `, gilded ${gilds} times` : ''}</div>
          </div>
          <div className="g-wrap" style={{ gap: '14px 18px' }}>
            {earned.length === 0 ? <p className="g-text2">No titles earned yet.</p> : earned.map((s) => {
              const node = defs.nodes.get(s)!;
              const name = defs.records.get(node.seal)?.title || node.name;
              return (
                <button key={s} type="button" className="g-sealmedal" title={`${name}\n${node.desc}\n\nClick to open this title in Triumphs`} onClick={() => nav(`/guardian/triumphs?seal=${s}`)}>
                  <Pic src={node.icon} width={72} height={72} />
                  <span>{name.toUpperCase()}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="g-legendblock">
        <SideLabel text="Exotic style" />
        <div className="g-stack" style={{ gap: 8, paddingRight: 30 }}>
          <div style={{ fontSize: 20 }}>Your exotic weapons, by kills</div>
          <p className="g-text2" style={{ fontSize: 13.5 }}>Every final blow you have landed with each exotic weapon, on all your characters together. The game only keeps this count for exotics.</p>
        </div>
        {!weapons ? <Asking text="Asking Bungie for your weapon kills..." /> : weapons.length === 0 ? <p className="g-text2">No exotic weapon kills recorded.</p> : (
          <div className="g-cols three" style={{ gap: 30 }}>
            {[0, 1, 2].map((c) => (
              <div key={c} className="g-stack" style={{ gap: 5 }}>
                {weapons.slice(c * 10, c * 10 + 10).map((w) => (
                  <button type="button" key={w.item} className="g-weaponrow" onClick={() => nav(`/guardian/operations/d2-info?item=${w.item}`)}
                    title={`${w.name}\n${Math.round(w.kills).toLocaleString()} kills, ${Math.round(w.precision).toLocaleString()} of them precision\n\nClick to open it in D2 Info`}>
                    <span className="g-tile" style={{ width: 42, height: 42 }}><Pic src={w.icon} /></span>
                    <span className="n">{w.name}</span>
                    <span>{Math.round(w.kills).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {!legend ? <Asking text={error ?? 'Asking Bungie for your lifetime numbers...'} /> : (
        [['PvE', legend.pve, PVE], ['Crucible', legend.pvp, PVP]].map(([title, from, which]) => (
          <div key={title as string} className="g-stack">
            <Heading>{(title as string).toUpperCase().split('').join(' ')}</Heading>
            <div className="g-wrap">
              {(which as [string, string][]).filter(([k]) => (from as any)[k]?.text).map(([k, label]) => {
                const v = (from as any)[k];
                return <StatTile key={k} label={label} value={v.value >= 1000 ? Math.round(v.value) : v.text} />;
              })}
            </div>
          </div>
        ))
      )}
    </>
  );
}
